/**
 * The room, as a fighter is allowed to see it — and what to do about it.
 *
 * `readFight` has been the definition of what the AI may know since step 48,
 * and the arena was simply not in it. A fighter driven by this module could be
 * standing a metre from a pillar with a train coming and know neither, which is
 * why STATE.md §7 has said since step 57 that two fighters on the roster are
 * *described* as environmental specialists and cannot be. This is the half that
 * makes them possible: the walls, the pillars, the pit and the train, read the
 * same way a body is read — as a flat snapshot of things that have already
 * resolved into the world.
 *
 * Three things are answered here and nothing else is:
 *
 *   `readArena`      where the hard surfaces are **from the other fighter**,
 *                    where the pit is, and whether a train is coming.
 *   `chooseTarget`   which of them this fighter would use, if any.
 *   `herdStick`      the step that walks the other one toward it.
 *
 * All of it is measured from the **opponent's** position rather than from the
 * fighter's own, because every one of these questions is about where somebody
 * else is going to end up. A wall two metres behind *you* is not a plan.
 *
 * **It is a trait, not a faculty.** Every fighter can see the room — that is
 * what `readArena` is — and only a fighter whose roster entry carries
 * `ringcraft` does anything with it. Tunnel and Pallbearer do; Metro is
 * technically clean and fights in the middle of the floor, and the file is what
 * says so.
 *
 * Pure, like everything in `core`: geometry over arena data, no three.js, no
 * renderer. It imports `surfaces.js` because the question "how far is that body
 * from that wall" already has one right answer and a second copy of it would
 * drift from the collision the moment a shape was added.
 */

import { depthFrom, normalAt, shapeOf } from './surfaces.js';
import { inPit } from './pit.js';

export class RingcraftConfigError extends Error {}

/**
 * The three things a fighter can be taught to do with a room, each a chance
 * between 0 and 1.
 *
 * Separate numbers rather than one, because they are three different fighters'
 * worth of behaviour and the roster wants to say so: Pallbearer walks people
 * into walls and does not go near the track, and Tunnel is the one who waits
 * for the service.
 */
export const RINGCRAFT_TRAITS = Object.freeze(['aim', 'herd', 'pit']);

/** A fighter who does none of it, which is everybody the file does not name. */
export const NO_RINGCRAFT = Object.freeze({ aim: 0, herd: 0, pit: 0 });

/** Nothing to read: no arena loaded, or an arena with nothing hard in it. */
const EMPTY_ROOM = Object.freeze({
  surfaces: Object.freeze([]),
  pit: null,
  train: null,
  any: false,
});

/**
 * Throws on a trait that could not mean what it says.
 *
 * A key that is not one of the three is the only thing it can be — a typo — and
 * a typo that is quietly ignored is a fighter who reads as an environmental
 * specialist in the file and fights in the middle of the floor. That is exactly
 * gotcha 29's shape, and this is where it gets caught.
 */
export function validateRingcraft(trait, { where = 'a fighter' } = {}) {
  if (trait === undefined || trait === null) return NO_RINGCRAFT;
  if (typeof trait !== 'object' || Array.isArray(trait)) {
    throw new RingcraftConfigError(`${where} has a ringcraft of ${JSON.stringify(trait)}`);
  }
  for (const [name, value] of Object.entries(trait)) {
    if (!RINGCRAFT_TRAITS.includes(name)) {
      throw new RingcraftConfigError(
        `${where} has a ringcraft of "${name}"; there is ${RINGCRAFT_TRAITS.join(', ')}`
      );
    }
    if (!(value >= 0 && value <= 1)) {
      throw new RingcraftConfigError(`${where} has ringcraft.${name} of ${JSON.stringify(value)}; want 0..1`);
    }
  }
  // A trait that turns nothing on is a roster entry that reads as a specialist
  // and fights like anybody else — the same lie as a key nobody reads.
  if (!RINGCRAFT_TRAITS.some((name) => (trait[name] ?? 0) > 0)) {
    throw new RingcraftConfigError(`${where} has a ringcraft where everything is zero; leave it out instead`);
  }
  return Object.freeze({ ...NO_RINGCRAFT, ...trait });
}

/** The nearest point of an axis-aligned region to a point on the floor. */
function nearestInRegion(point, region) {
  return {
    x: Math.min(Math.max(point.x, region.minX), region.maxX),
    z: Math.min(Math.max(point.z, region.minZ), region.maxZ),
  };
}

/**
 * The room, measured from where the other fighter is standing.
 *
 * Every hard surface gets the same two numbers — how far `foe` is from it, and
 * which way that is — so a wall, a pillar and a moving box are all one shape by
 * the time anything here has to choose between them. `toward` is the *reverse*
 * of the collision's outward normal, because the collision answers "which way
 * does a body come off this" and the question here is "which way do I send one
 * at it".
 *
 * Read **live**, not through the perception queue that delays what the AI knows
 * about the other fighter. Walls do not move, and the train announces itself
 * with three and a fifth seconds of headlights — longer than any tier's
 * reaction delay, so a delay on it would change nothing anybody could see. It
 * is the same reasoning that keeps the gap live: watching a room is continuous,
 * and it is *events* that cost time to notice.
 */
export function readArena({ foe, arena = null } = {}) {
  if (!foe || !arena) return EMPTY_ROOM;

  const surfaces = [];
  for (const surface of arena.surfaces ?? []) {
    if (!surface.hard) continue;
    const out = normalAt(foe, surface);
    surfaces.push(
      Object.freeze({
        id: surface.id,
        kind: 'surface',
        shape: shapeOf(surface),
        distance: depthFrom(foe, surface),
        toward: Object.freeze({ x: -out.x, z: -out.z }),
        damage: surface.damage ?? 0,
        lethal: false,
      })
    );
  }
  surfaces.sort((a, b) => a.distance - b.distance);

  // The train's own box is deliberately not in that list. It is a hard surface
  // while it is in the station, but a fighter sent at it goes over the platform
  // edge into the pit first — the pit is a region and is tested before anything
  // hard (gotcha 21) — so the thing to aim at is the hole, and the train is
  // what decides what the hole costs.
  const train = arena.train
    ? Object.freeze({
        phase: arena.train.phase ?? null,
        present: Boolean(arena.train.present),
        coming: arena.train.phase === 'approaching',
        eta: arena.train.eta ?? null,
      })
    : null;

  let pit = null;
  if (arena.pit) {
    const lip = nearestInRegion(foe, arena.pit);
    const dx = lip.x - foe.x;
    const dz = lip.z - foe.z;
    const distance = Math.hypot(dx, dz);
    pit = Object.freeze({
      id: arena.pit.id ?? 'pit',
      kind: 'pit',
      distance,
      toward:
        distance > 1e-6
          ? Object.freeze({ x: dx / distance, z: dz / distance })
          : Object.freeze({ x: 0, z: 1 }),
      inside: inPit(foe, arena.pit),
      /** What it costs right now, which is the train's business rather than the pit's. */
      lethal: Boolean(train?.present),
      /**
       * A drop worth sending somebody over whatever else is happening — a
       * roof's edge, rather than a track that only matters with a train in it.
       * The arena's data says so; nothing here guesses it from a depth.
       */
      sheer: arena.pit.sheer === true,
    });
  }

  return Object.freeze({
    surfaces: Object.freeze(surfaces),
    pit,
    train,
    any: surfaces.length > 0 || Boolean(pit),
  });
}

/**
 * Which part of the room this fighter would use, or null for none of it.
 *
 * Two rolls, in an order that is the whole personality of the thing: **the pit
 * first, and only when it is worth something.** An empty hole is twelve points
 * and a climb back out — less than a pillar — so a fighter aiming at it while
 * the platform is quiet is a fighter throwing away a wall. With a service in
 * the station it is the only thing in the game that does not care how healthy
 * anybody is, and `coming` is enough: the whip takes four tenths of a second
 * and the telegraph runs for three seconds, so a fighter who waits for the
 * train to *arrive* before starting has already missed it.
 *
 * `within` is how far the fighter is willing to send somebody — the whip's own
 * distance when aiming, and a wider look when deciding where to stand.
 */
export function chooseTarget(
  room,
  trait = NO_RINGCRAFT,
  { within = Infinity, trainWindow = 2.5, random = Math.random } = {}
) {
  if (!room?.any) return null;

  const { pit } = room;
  if (
    pit &&
    (trait.pit ?? 0) > 0 &&
    pit.distance <= within &&
    !pit.inside &&
    (pit.sheer ||
      room.train?.present ||
      (room.train?.coming && (room.train.eta ?? Infinity) <= trainWindow)) &&
    random() < trait.pit
  ) {
    return pit;
  }

  if ((trait.aim ?? 0) <= 0) return null;
  // Nearest first, which is also hardest first often enough: the pillars are
  // the far side of the walkable floor from the middle of the platform, and
  // they hit for more than the tiling does.
  const surface = room.surfaces.find((entry) => entry.distance <= within);
  if (!surface) return null;
  return random() < trait.aim ? surface : null;
}

/** One target again, by id, so a decision can be kept while its geometry moves. */
export function targetById(room, id) {
  if (!room || !id) return null;
  if (room.pit?.id === id) return room.pit;
  return room.surfaces.find((entry) => entry.id === id) ?? null;
}

/**
 * The step that walks the other fighter toward `target`.
 *
 * Nothing in this game lets one body push another across the floor, so herding
 * cannot be done by shoving. What it *can* be done by is standing somewhere: an
 * opponent closes on whoever they are fighting, so a fighter who keeps putting
 * themselves between the other one and a pillar is a fighter being followed to
 * it. The place to be is `foe + toward × range` — their side of the room, at
 * grappling distance.
 *
 * **It is an arc, not a line**, and that is the whole of why this is a function
 * rather than a point handed to `towards`. `locomotion` floors the gap at
 * `minSeparation`, so a fighter pushing straight at a spot on the far side of a
 * body walks into their chest and stays there for the rest of the round. What
 * moves a bearing is the *sideways* push; the forward one only holds the range
 * while it goes round.
 *
 * Returned in the stick's own basis — `x` slides around the opponent, `y`
 * closes the gap — which is what every other movement decision in the AI speaks.
 */
export function herdStick(self, foe, target, { range = 1 } = {}) {
  if (!self || !foe || !target) return { x: 0, y: 0 };
  const bx = self.x - foe.x;
  const bz = self.z - foe.z;
  const span = Math.hypot(bx, bz);
  // Standing on top of somebody has no bearing to correct. Any direction is as
  // good as any other, and none of them is a NaN.
  if (span < 1e-6) return { x: 1, y: 0 };
  const bearing = { x: bx / span, z: bz / span };
  const want = target.toward;

  const dot = bearing.x * want.x + bearing.z * want.z;
  const cross = bearing.x * want.z - bearing.z * want.x;
  /** How far round there is still to go: 0 already there, 1 the far side. */
  const off = Math.min(1, Math.max(0, (1 - dot) / 2));
  // Which way round is shorter. Exactly opposite has no shorter way, so it
  // picks one rather than standing still in the single place the arithmetic
  // cannot answer.
  const x = (cross > 1e-9 ? -1 : 1) * off;
  // The range is held while it circles, and closed once it is nearly there —
  // a fighter who walks in before they are round is a fighter in a clinch
  // pointing at open floor.
  const near = 1 - off;
  const y = span > range * 1.25 ? near : span < range ? -near : 0;

  const length = Math.hypot(x, y);
  return length > 1 ? { x: x / length, y: y / length } : { x, y };
}

/**
 * Where that step is walking to, for anything that wants the point rather than
 * the push — the debug overlay, and the tests that check the arc arrives.
 *
 * Frequently outside the walkable floor: the back wall is a metre and a half
 * past the edge of it. That is correct rather than a bug — `locomotion` clamps
 * every step to the arena's bounds, so walking at a point inside a wall is
 * walking until your back is against it.
 */
export function herdSpot(foe, target, range = 1) {
  if (!foe || !target) return null;
  return {
    x: foe.x + target.toward.x * range,
    z: foe.z + target.toward.z * range,
  };
}

/**
 * Whether the other fighter is already near enough to `target` to be sent at it.
 *
 * The whip has a fixed distance and a body that does not reach the wall simply
 * lands on the floor next to it, which is a grapple spent on nothing.
 */
export function withinReach(target, distance) {
  return Boolean(target) && target.distance <= distance;
}
